import { State } from '@meraki-internal/state';
import { OrganizationsService } from './OrganizationsService';
import { IOrganization, IOrganizationBaseUser, IOrganizationUser, IOrganizationRole } from './IOrganization';
import { MaybeWithLinks, WithLinks } from '../caremap/WithLinks';
import { ICareMap, ILightCareMap } from '../caremap/ICareMap';
import { CareMapState, MYSELF } from '../caremap/CareMapState';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { UserFacingError } from '../innerhive-plus/revenue-cat/UserFacingError';
import { AsyncStorageProvider, IAsyncSmartStorageProvider } from '../support/AsyncStorageProvider';
import { IResourceChangedSocketMessage, IWebSocketsConnected } from '../web-sockets/SocketState';
import { Logger } from '../support/Logger';

// A transfer can move several care maps at once, each sending its own message, so a burst is read as
// one. The wait also lets the share index the roster is read from catch up with the change that sent
// the message.
export const ORGANIZATION_REFRESH_DELAY_MS = 1500;

export class OrganizationsState extends State<Record<string, never>> {
    static inject = () => [
        OrganizationsService,
        CareMapState,
        AsyncStorageProvider,
        BufferedEventBus,
        Logger
    ];
    constructor(
        private api: OrganizationsService,
        private caremaps: CareMapState,
        private storageProvider: AsyncStorageProvider,
        private events: BufferedEventBus,
        private logger: Logger
    ){
        super({});

        // load all orgs when accepting invite because you are in caremap context
        // so there's no _activeOrganization to only load that one orgs info
        this.events.on('InvitationService.accepted', this.load);

        // Who holds each client decides who can open them, so the roster follows a change of holder as
        // it happens, and catches up on any it missed while the socket was down.
        this.events.on('web-sockets.message', this.onWebsocketMessage);
        this.events.on('web-sockets.connected', this.catchUp);

        this.lastActiveOrgIdStorage = this.storageProvider.getStringProvider('last-active-org-id');
    }

    private pendingRefreshes: { [orgId: string]: ReturnType<typeof setTimeout> } = {};

    // Two reads of the same list can overlap and return in either order. A response is applied unless a
    // newer one already has been, so a caller awaiting its own read always sees data at least that fresh.
    private appliedRead: { [listAndOrgId: string]: number } = {};
    private readSequence = 0;

    private onWebsocketMessage = (message?: IResourceChangedSocketMessage) => {
        const resource = message?.resourceToRefresh?.toLowerCase();
        if (!resource || !this.organizations) {
            return;
        }
        const org = this.organizations.find(candidate => candidate.links?.caremaps?.href.toLowerCase() === resource);
        if (org) {
            this.refreshSoon(org.orgId);
        }
    };

    private catchUp = (connected?: IWebSocketsConnected) => {
        if (!this.organizations || !connected?.mayHaveMissedMessages) {
            return;
        }
        for (const org of this.getNonClientOrganizations()) {
            this.refreshSoon(org.orgId);
        }
    };

    // The users are read too: each client's transfer link depends on who holds the client.
    private refreshSoon = (orgId: string) => {
        clearTimeout(this.pendingRefreshes[orgId]);
        this.pendingRefreshes[orgId] = setTimeout(async () => {
            delete this.pendingRefreshes[orgId];
            try {
                await this.loadOrganizationUsers(orgId);
                await this.loadOrganizationCareMaps(orgId);
            } catch (err) {
                // What is on screen stays until the next message or reconnect reads it again.
                this.logger.error(err, { context: 'OrganizationsState could not refresh the roster', orgId });
            }
        }, ORGANIZATION_REFRESH_DELAY_MS);
    };

    private startRead = (key: string): (() => boolean) => {
        const read = ++this.readSequence;
        return () => {
            if ((this.appliedRead[key] ?? 0) > read) {
                return false;
            }
            this.appliedRead[key] = read;
            return true;
        };
    };

    private organizations!: MaybeWithLinks<IOrganization>[];

    private lastActiveOrgIdStorage: IAsyncSmartStorageProvider<string>;
    private lastActiveOrgId?: string;

    private _activeOrganization?: IOrganization;

    setActiveOrganization = (orgId: string) => {
        const org = this.getOrganization(orgId);

        if (!org) {
            throw Error(`Organization with ID ${orgId} not found`);
        }

        this._activeOrganization = org;
        this.setState({ });

        this.lastActiveOrgId = orgId;
        this.lastActiveOrgIdStorage.set(orgId);
    };

    getMostRecentOrganization = (): IOrganization | undefined => {
        const active = this._activeOrganization;
        if (active) {
            return active;
        }
        if (this.lastActiveOrgId) {
            const last = this.getOrganization(this.lastActiveOrgId);
            if (last) {
                return last;
            }
        }
        return this.organizations[0];
    };

    getNonClientOrganizations = () => {
        return this.organizations.filter(org => org.role !== 'org-client');
    };

    getAllOrganizations = () => this.organizations;

    isOrgTeamMember = () => {
        return this.getNonClientOrganizations().length > 0;
    };

    getOrganization = (orgId: string) => this.organizations.find(o => o.orgId === orgId);
    getOrganizationOrThrow = (orgId: string) => {
        const org = this.getOrganization(orgId);
        if (!org){
            throw new Error(`cannot find org ${orgId}`);
        }
        return org;
    };

    private organizationUsers: {
        [orgId: string]: WithLinks<IOrganizationUser>[]
    } = {};

    private organizationCareMaps: {
        [orgId: string]: ILightCareMap[]
    } = {};

    getOrganizationUser = ({ orgId, userId }: { orgId: string, userId: string; }) => {
        const users = this.organizationUsers[orgId];
        return users ? users.find(u => u.userId === userId) : undefined;
    };

    load = async () => {
        this.organizations = await this.api.getOrganizations();
        await Promise.all(this.organizations.map(o => this.loadOrganizationUsers(o.orgId)));
        await Promise.all(this.organizations.map(o => this.loadOrganizationCareMaps(o.orgId)));

        this.lastActiveOrgId = await this.lastActiveOrgIdStorage.get();
    };

    createOrganization = async (org: { name: string; }) => {
        const res = await this.api.createOrganization(org);
        await this.load();
        return res;
    };

    deleteOrganization = async (orgId: string) => {
        // TODO: not found, handle?
        await this.api.deleteOrganization(this.getOrganization(orgId)!);
        await this.load();
    };

    canAddOrganizationUser = (orgId: string, userType: IOrganizationRole) => {
        const org = this.getOrganization(orgId);
        return org?.links.users && org.links.users.actions?.includes(`add-org-users--${userType}`);
    };

    canRemoveOrganizationUser = (user: MaybeWithLinks<IOrganizationUser>) => {
        return user?.links?.self && user.links.self.actions?.includes(`remove-org-user--${user.role}`);
    };

    canAddClientCareMap = (user: MaybeWithLinks<IOrganizationUser>) => {
        return user.links?.caremaps?.actions?.includes('create-caremap');
    };

    canViewClientCareMap = (map: ILightCareMap): boolean => {
        return Boolean(map.links.self);
    };

    hasUnreadableCareMap = (careMaps: ILightCareMap[]): boolean => {
        return careMaps.some(map => !this.canViewClientCareMap(map));
    };

    /** The colleagues holding a share on the care maps among these that this person cannot open. */
    getTeamMembersHolding = ({ orgId, careMaps }: { orgId: string; careMaps: ILightCareMap[] }): IOrganizationUser[] => {
        const holderIds = new Set(careMaps
            .filter(map => !this.canViewClientCareMap(map))
            .flatMap(map => map.orgShares.map(share => share.userId)));
        return this.getOrganizationTeamMembers(orgId).filter(member => holderIds.has(member.userId));
    };

    private loadOrganizationUsers = async (orgId: string) => {
        const org = this.getOrganization(orgId);
        if (org) {
            // can't load users if i'm not allowed to
            if (org.links.users) {
                const claimNewest = this.startRead(`users/${org.orgId}`);
                const users = await this.api.getOrganizationUsers(org);
                if (!claimNewest()) {
                    return;
                }
                this.organizationUsers[org.orgId] = users;
                this.setState({ });
            } else {
                this.organizationUsers[org.orgId] = [];
            }
        }
    };

    loadOrganizationCareMaps = async (orgId: string) => {
        const org = this.getOrganization(orgId);
        if (org && org.links.caremaps?.actions?.includes('get-caremaps')) {
            // can't load caremaps if i'm not allowed to
            if (org.links.caremaps) {
                const claimNewest = this.startRead(`caremaps/${org.orgId}`);
                const maps = await this.api.getOrganizationCareMaps(org);
                if (!claimNewest()) {
                    return;
                }
                this.organizationCareMaps[org.orgId] = maps;
                this.setState({ });
            } else {
                this.organizationCareMaps[org.orgId] = [];
            }
        }
    };

    getOrganizationUsers = (orgId: string) => {
        return this.organizationUsers[orgId] || [];
    };

    getOrganizationTeamMembers = (orgId: string) => {
        return this.getOrganizationUsers(orgId).filter(user => user.role !== 'org-client');
    };

    getOrganizationClientUsers = (orgId: string) => {
        return this.getOrganizationUsers(orgId).filter(user => user.role === 'org-client');
    };

    addOrganizationClient = async (orgId: string, client: IOrganizationBaseUser) => {
        const org = this.getOrganizationOrThrow(orgId);
        const userId = await this.api.createOrganizationClient(org, {...client, ...{role: 'org-client'}});
        await this.loadOrganizationUsers(org.orgId);
        await this.loadOrganizationCareMaps(org.orgId);

        const hasCaremaps = this.getOrganizationCareMapsForUserV2({ orgId: org.orgId, userId }).length > 0;
        if (hasCaremaps){
            throw new UserFacingError({
                // the api operation was successful, hence 200, but from a client perspective, its not in the state we want it to be in
                // hence throwing this error. (409 was another option, but that is also being used for a duplicate, and it suggests the
                // operation failed.)
                status: 200,
                errorCode: 'client-added-with-caremaps',
                message: `userId ${userId} was added to the organization as a client, but already has caremaps`,
                displayMessage: 'We found the user with that email and they were added to your organization. They already have at least one caremap. For their privacy, you must request that they add you as a co owner to the caremaps they want your help managing, to gain access to their caremaps.',
                displayHeader: 'User Added',
            });
        }

        return userId;
    };

    addClientCaremap = async ({ orgId, userId }: { orgId: string; userId: string; }) => {
        const users = this.getOrganizationClientUsers(orgId);
        const user = users.find(u => u.userId === userId);
        if (!user){
            throw new Error(`user ${userId} not found`);
        }
        // TODO: refactor to getUser
        const { careMapId } = await this.caremaps.addCaremapToClientUser(user);

        return { careMapId };
    };

    addOrganizationUser = async (orgId: string, user: IOrganizationBaseUser) => {
        const org = this.getOrganizationOrThrow(orgId);
        const userId = await this.api.createOrganizationUser(org, user);
        await this.loadOrganizationUsers(org.orgId);
        return userId;
    };

    removeUser = async (user: IOrganizationUser) => {
        await this.api.removeOrganizationUser(user);

        if (user.role === 'org-client'){
            await this.caremaps.removeOrgClient(user);
        }

        // note we used to re-fetch the org users from the API
        // but we saw test flake, likely due to eventually consistency (org-users is on an index)
        let orgUsers = this.organizationUsers[user.orgId] || [];
        orgUsers = orgUsers.filter(u => u.userId !== user.userId);
        this.organizationUsers[user.orgId] = orgUsers;

        this.setState({ });
    };

    getOrganizationCareMaps = (orgId: string) => {
        return this.organizationCareMaps[orgId] || [];
    };

    getOrganizationCareMapsForUser = (orgId: string, userId: string) => {
        const orgMaps = this.getOrganizationCareMaps(orgId);
        return orgMaps.filter(map => map.userId === userId);
    };

    getOrganizationCareMapsForUserV2 = ({ orgId, userId }: { orgId: string; userId: string; }) => {
        const orgMaps = this.getOrganizationCareMaps(orgId);
        return orgMaps.filter(map => map.userId === userId);
    };

    getClientsCoOwnedByUser = ({ orgId, teamMemberId }: { orgId: string, teamMemberId: string }): MaybeWithLinks<IOrganizationUser>[] => {
        return this.getOrganizationClientUsers(orgId)
            .filter(client => {
                const caremapsOwnedByClient = this.getOrganizationCareMapsForUser(orgId, client.userId);
                const hasAnyCaremapsDirectlySharedToTeamMember = caremapsOwnedByClient.some(m => m.orgShares.some(share => share.userId === teamMemberId));

                return hasAnyCaremapsDirectlySharedToTeamMember;
            });
    };

    userHasClientCoOwnedCareMaps = (orgId: string, userId: string) => {
        return this.getClientsCoOwnedByUser({ orgId, teamMemberId: userId }).length > 0;
    };

    private getAllCaremaps = () => {
        let caremaps: ILightCareMap[] = [];
        for (const orgId of Object.keys(this.organizationCareMaps)){
            caremaps = [
                ...caremaps,
                ...this.organizationCareMaps[orgId]
            ];
        }
        return caremaps;
    };

    getOrganizationCareMap = (caremapId: string) => {
        return this.getAllCaremaps().find(c => c.careMapId === caremapId);
    };

    transferClients = async ({ orgId, fromMemberId, toMemberId }: { orgId: string; fromMemberId: string; toMemberId: string; }) => {
        const clients = this.getClientsCoOwnedByUser({ orgId, teamMemberId: fromMemberId });

        for (const clientUser of clients){
            await this.api.transferClient({
                clientUser: clientUser as WithLinks<IOrganizationUser>,
                fromMemberId,
                toMemberId,
            });
        }

        await this.loadOrganizationCareMaps(orgId);
    };

    canTransferClients = (user: MaybeWithLinks<IOrganizationUser>) => {
        const orgId = user.orgId;

        const hasCoOwnedMaps = this.userHasClientCoOwnedCareMaps(orgId, user.userId);
        return hasCoOwnedMaps && user.links && user.links['transfer-caremaps'] && this.getOrganizationTeamMembers(orgId).length > 1;
    };

    isClientAlsoCareRecipient = (caremap: ICareMap | undefined): boolean => {
        if (!caremap){
            return false;
        }
        if (!caremap.initialWizardAnswers || !caremap.initialWizardAnswers.recipient){
            // if it is missing this property it was likely created by our tests and doesn't matter
            // but in the case of an org client they are most likely to have one, and for them selves
            return true;
        }
        return caremap.initialWizardAnswers['recipient'].value === MYSELF.value;
    };
};
